import { useMemo } from "react";
import { ReactFlow, Background, Controls, type Edge, type Node } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { FlowchartHint, WorkflowPlan } from "../lib/types";
import { projectFlowchart } from "../lib/flowchart";

export function Flowchart({ plan, hints }: { plan: WorkflowPlan; hints: FlowchartHint[] }) {
  const projection = useMemo(() => projectFlowchart(plan, hints), [plan, hints]);
  const nodes: Node[] = projection.nodes.map((node, index) => ({
    id: node.id,
    position: { x: node.x ?? (index % 4) * 220, y: node.y ?? Math.floor(index / 4) * 140 },
    data: { label: node.label },
    className: node.kind === "verify" ? "tj-node-verify" : node.kind === "branch" ? "tj-node-branch" : "",
  }));
  const edges: Edge[] = projection.edges.map((edge) => ({
    id: edge.id,
    source: edge.from,
    target: edge.to ?? edge.from,
    label: edge.label,
    animated: edge.escalate,
    style: edge.escalate ? { strokeDasharray: "5 5" } : undefined,
  }));

  return (
    <div className="space-y-3">
      <div className="hidden h-[420px] rounded-xl border border-[var(--tj-border)] sm:block">
        <ReactFlow nodes={nodes} edges={edges} fitView>
          <Background />
          <Controls />
        </ReactFlow>
      </div>
      {/* Mobile fallback: vertical path list with condition chips. */}
      <ol className="space-y-2 sm:hidden" aria-label="Workflow path list">
        {projection.nodes.map((node) => (
          <li key={node.id} className="rounded-lg border border-[var(--tj-border)] p-2">
            <p className="text-sm font-medium">{node.mobileLabel}</p>
            <p className="text-xs text-[var(--tj-muted)]">{node.kind}</p>
            {projection.edges
              .filter((edge) => edge.from === node.id && edge.label)
              .map((edge) => (
                <span key={edge.id} className="mt-1 mr-1 inline-block rounded-full bg-[var(--tj-surface)] px-2 py-0.5 text-xs">
                  {edge.label}
                </span>
              ))}
          </li>
        ))}
      </ol>
    </div>
  );
}
